import { FC, ReactNode } from 'react'
import { Form, Formik, FormikHelpers } from 'formik'
import { useQueryClient } from '@tanstack/react-query'
import Button from 'antd/lib/button'

import { renameProject } from '@pepper/utils/src/api/projects/projects'
import { Modal, useGlobalState } from '@pepper/ui'
import { getFieldComponent } from 'components/FormFields/FormFields'

import { formFields, validationSchema } from './schema'
import styles from './_.module.css'

interface Props {
  isOpen: boolean
  onCancel: () => void
  projectId?: string
  projectName?: string
  onRename?: () => void
}

interface Values {
  projectName: string
}

const ProjectActionsModal: FC<Props> = ({
  isOpen,
  onCancel,
  projectId = '',
  projectName = '',
  onRename,
}) => {
  const queryClient = useQueryClient()

  const { setPendingSidebarCountKeys } = useGlobalState()
  const initialValues: Values = { projectName }

  const handleSubmit = async (
    values: Values,
    action: FormikHelpers<Values>
  ) => {
    try {
      const { status, error } = await renameProject({
        projectId,
        body: { project_title: values.projectName },
      })
      if (status === 'ok') {
        // Update project list after project rename
        queryClient.invalidateQueries([[`${projectId}/details`]])
        setPendingSidebarCountKeys(['allProjectsCount'])
        action.resetForm()
        onRename?.()
        onCancel()
      } else {
        action.setFieldError('projectName', error)
      }
    } catch (error) {
      throw new Error(error)
    }
  }

  const handleOnCancel = (resetForm: FormikHelpers<Values>['resetForm']) => {
    resetForm()
    onCancel()
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {(props): ReactNode => (
          <Modal
            title="Rename the Project"
            visible={isOpen}
            centered={true}
            className={styles.modal}
            onCancel={() => handleOnCancel(props.resetForm)}
            footer={[
              <Button
                key="cancel"
                onClick={() => handleOnCancel(props.resetForm)}
              >
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                onClick={props.submitForm}
                loading={props.isSubmitting}
                disabled={props.isSubmitting || !props.isValid || !props.dirty}
                data-id="business-project-actions-modal-save-changes-button"
              >
                Save Changes
              </Button>,
            ]}
            closable={true}
            destroyOnClose={true}
          >
            <Form>
              <section className={styles.formSection}>
                {formFields.map(field => (
                  <div key={field.name}>
                    {getFieldComponent({
                      type: field.type,
                      name: field.name,
                      label: field.label,
                      className: field.className,
                      required: field.required,
                      actions: props,
                    })}
                  </div>
                ))}
              </section>
            </Form>
          </Modal>
        )}
      </Formik>
    </div>
  )
}

export default ProjectActionsModal
